import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { PageHeader, LoadingSpinner } from '../../components/UI';
import toast from 'react-hot-toast';
import wardsData from '../../data/wards.json';

export default function WorkflowRoutingMaster() {
  const [nocTypes, setNocTypes]   = useState([]);
  const [depts, setDepts]         = useState([]);
  const [selected, setSelected]   = useState(null);
  const [steps, setSteps]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);

  // Sarai zone routing
  const [isSarai, setIsSarai]         = useState(false);
  const [zoneDepts, setZoneDepts]     = useState({ 1:'', 2:'', 3:'', 4:'', 5:'' });
  const [savingZones, setSavingZones] = useState(false);

  const wards    = wardsData.wards  || [];
  const zones    = wardsData.zones  || [];

  useEffect(() => {
    Promise.all([
      api.get('/masters/noc-types'),
      api.get('/masters/departments'),
    ]).then(([n, d]) => {
      setNocTypes(n.noc_types || []);
      setDepts(d.departments || []);
    });
  }, []);

  const loadRouting = async (noc) => {
    setSelected(noc);
    setLoading(true);

    // Check if Sarai NOC
    const saraiFlag = noc.noc_name?.toLowerCase().includes('sarai') ||
                      noc.noc_code?.toLowerCase().includes('sarai');
    setIsSarai(saraiFlag);

    try {
      const r = await api.get(`/workflow-routing/${noc.id}`);
      const existing = r.routing || [];
      if (existing.length > 0) {
        setSteps(existing.map(s => ({
          id: s.id,
          dept_id: String(s.dept_id),
          sequence_order: s.sequence_order,
          is_final_approval: s.is_final_approval,
          dept_name: s.dept_name,
        })));
      } else {
        const init = [];
        if (noc.related_dept_id && noc.related_dept_id !== noc.final_dept_id) {
          init.push({ dept_id: String(noc.related_dept_id), sequence_order: 1, is_final_approval: 0 });
        }
        if (noc.final_dept_id) {
          init.push({ dept_id: String(noc.final_dept_id), sequence_order: init.length + 1, is_final_approval: 1 });
        }
        setSteps(init.length > 0 ? init : [{ dept_id: '', sequence_order: 1, is_final_approval: 1 }]);
      }

      // Load zone depts for Sarai
      if (saraiFlag) {
        try {
          const zonesRes = await api.get('/zones');
          const map = {};
          (zonesRes.zones || []).forEach(z => {
            map[z.id] = z.dept_id ? String(z.dept_id) : '';
          });
          setZoneDepts(map);
        } catch (e) { console.log('zones error', e.message); }
      }
    } finally { setLoading(false); }
  };

  const addStep = () => {
    const maxSeq = steps.length > 0 ? Math.max(...steps.map(s => s.sequence_order)) : 0;
    setSteps(s => [...s, { dept_id: '', sequence_order: maxSeq + 1, is_final_approval: 0 }]);
  };

  const removeStep = (i) => {
    if (steps.length === 1) return toast.error('At least one step required');
    const updated = steps.filter((_, idx) => idx !== i)
      .map((s, idx) => ({ ...s, sequence_order: idx + 1 }));
    updated[updated.length - 1].is_final_approval = 1;
    updated.slice(0, -1).forEach(s => { s.is_final_approval = 0; });
    setSteps(updated);
  };

  const moveStep = (i, dir) => {
    const arr = [...steps];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    const reordered = arr.map((s, idx) => ({ ...s, sequence_order: idx + 1 }));
    reordered[reordered.length - 1].is_final_approval = 1;
    reordered.slice(0, -1).forEach(s => { s.is_final_approval = 0; });
    setSteps(reordered);
  };

  const updateStep = (i, key, val) => {
    setSteps(s => s.map((step, idx) => idx === i ? { ...step, [key]: val } : step));
  };

  const handleSave = async () => {
    if (steps.some(s => !s.dept_id)) return toast.error('Please select department for all steps');
    setSaving(true);
    try {
      const stepsToSave = steps.map((s, i) => ({
        dept_id: s.dept_id,
        sequence_order: i + 1,
        is_final_approval: i === steps.length - 1 ? 1 : 0,
      }));
      await api.post('/workflow-routing', { noc_type_id: selected.id, steps: stepsToSave });
      toast.success('Workflow routing saved!');
      loadRouting(selected);
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleSaveZones = async () => {
    setSavingZones(true);
    try {
      for (const [zoneId, deptId] of Object.entries(zoneDepts)) {
        await api.put('/zones/dept', { zone_id: parseInt(zoneId), dept_id: deptId || null });
      }
      toast.success('Zone departments saved!');
    } catch { toast.error('Failed'); }
    finally { setSavingZones(false); }
  };

  const deptOptions = depts.filter(d => d.is_active !== 0);

  return (
    <div>
      <PageHeader
        title="⚙️ Workflow Routing Master"
        subtitle="Configure department approval sequence for each NOC type"
      />

      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
        <p className="font-semibold mb-1">ℹ️ How It Works</p>
        <p className="text-xs text-blue-700">
          Define the order of departments. Last department = Final Approver.
          For <strong>Sarai NOC</strong>: extra zone routing section will appear below to assign departments per zone.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* NOC List */}
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-3">Select NOC Type</h3>
          <div className="space-y-2">
            {nocTypes.map(noc => (
              <button key={noc.id} onClick={() => loadRouting(noc)}
                className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-all
                  ${selected?.id === noc.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300 text-gray-700'}`}>
                <p className="font-semibold">{noc.noc_name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{noc.noc_code}</p>
                {(noc.noc_name?.toLowerCase().includes('sarai') ||
                  noc.noc_code?.toLowerCase().includes('sarai')) && (
                  <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded mt-1 inline-block">
                    🏘️ Zone routing
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Routing Builder */}
        <div className="lg:col-span-2 space-y-4">
          {!selected ? (
            <div className="card text-center py-16 text-gray-400">
              <p className="text-4xl mb-3">👈</p>
              <p>Select a NOC type to configure its workflow</p>
            </div>
          ) : loading ? (
            <LoadingSpinner size="lg" className="py-20" />
          ) : (
            <>
              {/* Standard routing */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800">
                    Routing: <span className="text-blue-600">{selected.noc_name}</span>
                    {isSarai && <span className="ml-2 text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Zone-based</span>}
                  </h3>
                  <div className="flex gap-2">
                    <button onClick={addStep} className="btn-secondary text-xs">+ Add Step</button>
                    <button onClick={handleSave} className="btn-primary text-xs" disabled={saving}>
                      {saving ? 'Saving...' : '💾 Save'}
                    </button>
                  </div>
                </div>

                {/* Visual flow */}
                <div className="flex items-center gap-2 flex-wrap mb-6 p-3 bg-gray-50 rounded-xl overflow-x-auto">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 border-2 border-blue-400 rounded-full flex items-center justify-center">👤</div>
                    <p className="text-xs mt-1 text-gray-500">Citizen</p>
                  </div>
                  {steps.map((step, i) => {
                    const dept = depts.find(d => String(d.id) === String(step.dept_id));
                    return (
                      <React.Fragment key={i}>
                        <span className="text-gray-300 text-xl flex-shrink-0">→</span>
                        <div className={`flex flex-col items-center flex-shrink-0 p-2 rounded-xl border-2
                          ${i === steps.length - 1 ? 'border-purple-400 bg-purple-50' : 'border-orange-300 bg-orange-50'}`}>
                          <div className="text-lg">{i === steps.length - 1 ? '🏛️' : '🏢'}</div>
                          <p className="text-xs font-medium text-center max-w-[90px] leading-tight mt-1">
                            {dept ? dept.dept_name : <span className="text-gray-400">Not set</span>}
                          </p>
                          {i === steps.length - 1 && (
                            <span className="text-xs text-purple-600 font-bold">
                              {isSarai ? 'Zone Dept' : 'Final'}
                            </span>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <span className="text-gray-300 text-xl flex-shrink-0">→</span>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 border-2 border-green-400 rounded-full flex items-center justify-center">✅</div>
                    <p className="text-xs mt-1 text-gray-500">Issued</p>
                  </div>
                </div>

                {/* Step editor */}
                <div className="space-y-3">
                  {steps.map((step, i) => (
                    <div key={i}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2
                        ${i === steps.length - 1 ? 'border-purple-200 bg-purple-50' : 'border-gray-200 bg-white'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0
                        ${i === steps.length - 1 ? 'bg-purple-200 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                        {i + 1}
                      </div>
                      <select className="form-select text-sm flex-1" value={step.dept_id}
                        onChange={e => updateStep(i, 'dept_id', e.target.value)}>
                        <option value="">-- Select Department --</option>
                        {deptOptions.map(d => (
                          <option key={d.id} value={String(d.id)}>{d.dept_name} ({d.dept_type})</option>
                        ))}
                      </select>
                      {i === steps.length - 1 ? (
                        <span className="text-xs bg-purple-100 text-purple-700 border border-purple-300 px-2 py-1 rounded-full flex-shrink-0 font-medium">
                          {isSarai ? '🏘️ Zone Override' : '🏛️ Final'}
                        </span>
                      ) : (
                        <span className="text-xs bg-orange-50 text-orange-600 border border-orange-200 px-2 py-1 rounded-full flex-shrink-0">
                          🏢 Related
                        </span>
                      )}
                      <div className="flex flex-col gap-0.5 flex-shrink-0">
                        <button onClick={() => moveStep(i, -1)} disabled={i === 0}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-20 text-xs">▲</button>
                        <button onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-20 text-xs">▼</button>
                      </div>
                      <button onClick={() => removeStep(i)}
                        className="text-red-400 hover:text-red-600 text-xl leading-none flex-shrink-0">✕</button>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <button onClick={addStep}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                    ➕ Add Department Step
                  </button>
                  <button onClick={handleSave} className="btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : '💾 Save Routing'}
                  </button>
                </div>
              </div>

              {/* ── Sarai Zone Routing — extra section ── */}
              {isSarai && (
                <div className="card border-2 border-orange-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-800">🏘️ Sarai Zone Department Assignment</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Each zone gets its own final approval department.
                        The last step above will be <strong>automatically replaced</strong> by the zone dept when citizen applies.
                      </p>
                    </div>
                    <button onClick={handleSaveZones} disabled={savingZones}
                      className="btn-warning text-sm">
                      {savingZones ? 'Saving...' : '💾 Save Zones'}
                    </button>
                  </div>

                  {/* Zone flow diagram */}
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl mb-4 text-xs text-orange-800">
                    <p className="font-semibold mb-1">Flow for Sarai NOC:</p>
                    <code className="block">
                      Citizen (Ward X) → Zone detected →
                      {steps.filter(s => !s.is_final_approval).map((s, i) => {
                        const d = depts.find(dep => String(dep.id) === String(s.dept_id));
                        return ` ${d?.dept_name || 'Step ' + (i+1)} →`;
                      })}
                      {' Zone N Office → ✅ Approved'}
                    </code>
                  </div>

                  <div className="space-y-3">
                    {zones.map(zone => {
                      const zoneWards = wards.filter(w => w.zone_id === zone.id);
                      return (
                        <div key={zone.id}
                          className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-700 font-bold flex-shrink-0">
                            Z{zone.id}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-800">{zone.zone_name}</p>
                            <p className="text-xs text-gray-400">
                              Wards: {zoneWards.map(w => w.ward_no).join(', ')}
                            </p>
                          </div>
                          <span className="text-gray-300 text-lg flex-shrink-0">→</span>
                          <div className="flex-1">
                            <select className="form-select text-sm"
                              value={zoneDepts[zone.id] || ''}
                              onChange={e => setZoneDepts(p => ({ ...p, [zone.id]: e.target.value }))}>
                              <option value="">-- Not Assigned --</option>
                              {deptOptions.map(d => (
                                <option key={d.id} value={String(d.id)}>
                                  {d.dept_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-shrink-0">
                            {zoneDepts[zone.id]
                              ? <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-1 rounded-full">✅</span>
                              : <span className="text-xs bg-red-50 text-red-400 border border-red-200 px-2 py-1 rounded-full">⚠️</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Ward reference */}
                  <details className="mt-4">
                    <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                      📋 View Ward → Zone reference table
                    </summary>
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {wards.map(w => {
                        const z = zones.find(z => z.id === w.zone_id);
                        return (
                          <div key={w.id} className="text-xs p-2 bg-gray-50 rounded-lg border border-gray-100">
                            <span className="font-semibold text-gray-700">Ward {w.ward_no}</span>
                            <span className="text-gray-400 mx-1">→</span>
                            <span className="text-blue-600 font-medium">{z?.zone_name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </details>
                </div>
              )}

              {/* Info */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600 space-y-1">
                <p className="font-semibold text-gray-700">💡 Rules:</p>
                <p>• Last step = Final Approver automatically</p>
                <p>• Single department = it is both reviewer AND final approver</p>
                <p>• For Sarai: last step is replaced by citizen's zone department at submission</p>
                <p>• Use ▲▼ to reorder steps</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}